/* eslint-disable no-nested-ternary */
import { FC, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { CustomerAddress } from 'src/interfaces/new-channel-partner/channelPartner';
import { PaginationAndFilter } from 'src/interfaces/common/pagination';
import { pincodeApi } from 'src/api/pincode';
import { pincodeRegex } from 'src/components/common/common-regex';
import { AddressType } from 'src/enums/common';
import { InputField } from '../../../input-field';

interface AddCustomerProfileProps {
  open: boolean;
  onClose: () => void;
  address?: CustomerAddress;
  addAddress: (data: CustomerAddress) => Promise<boolean>;
}

export const AddCustomerAddress: FC<AddCustomerProfileProps> = (props) => {
  const { open, onClose, address, addAddress } = props;
  const [checkPincode, setCheckPincode] = useState<boolean>(false);
  const [areas, setAreas] = useState([]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      addressType: address
        ? ((address.name !== AddressType.HOME && address.name !== AddressType.WORK)
          ? AddressType.OTHER
          : address.name)
        : AddressType.HOME,
      saveAddressAs: address && (
        address.name !== AddressType.HOME && address.name !== AddressType.WORK
      )
        ? address.name
        : '',
      addressLine1: '',
      addressLine2: '',
      landmark: '',
      pincode: null,
      city: '',
      state: '',
      area: '',
      submit: null
    },
    validationSchema: Yup.object().shape({
      saveAddressAs: Yup.string().when('addressType', ([addressType], schema) => {
        if (addressType === AddressType.OTHER) { return Yup.string().required('Field is required'); }
        return schema;
      }),
      addressType: Yup.string().required('AddressType is required'),
      addressLine1: Yup.string().required('Address Line 1 is required'),
      addressLine2: Yup.string().optional(),
      landmark: Yup.string().optional(),
      area: Yup.string().required('Area is required'),
      pincode: Yup.number().moreThan(0).min(100000, 'Not a valid Pincode')
        .max(999999, 'Not a valid Pincode')
        .required('Valid Pincode is Required')
        .typeError('Valid Pincode is Required'),
      city: Yup.string().required('City is required'),
      state: Yup.string().required('State is required')
    }),

    onSubmit: async (values, helpers) => {
      const editAddress: CustomerAddress = {
        name: values.addressType === AddressType.OTHER
        && values.saveAddressAs ? values.saveAddressAs : values.addressType,
        city: values.city,
        line1: values.addressLine1,
        line2: values.addressLine2,
        landmark: values.landmark,
        pincode: values.pincode,
        area: values.area,
        state: values.state,
        default: false
      };
      const result = addAddress(editAddress);
      if (result) {
        onClose();
        helpers.resetForm();
      }
    }
  });

  // this method maintain the original values when we close the dialog
  const closeDialog = (e, reason) => {
    if (reason !== 'backdropClick') {
      formik.resetForm();
      onClose();
    }
  };

  const setPincodeDetails = (pincodes) => {
    // Set city
    formik.setFieldValue('city', pincodes[0]?.city);
    // set state
    formik.setFieldValue('state', pincodes[0]?.state);
    // set areas for dropdown
    const areaList = [];
    // get areas from each pincode onject
    pincodes?.forEach((pincode) => areaList.push(pincode.area));
    // set areas using distinct values from areaList
    setAreas([...new Set(areaList)]);
  };

  const handlePincodeChange = async (event) => {
    formik.setFieldValue('pincode', event.target.value);
    if (pincodeRegex.test(event.target.value?.toString())) {
      await formik.setFieldTouched('pincode', true);
      const paginationObject: PaginationAndFilter = {
        filters: [
          {
            property: 'code',
            operator: 'equal',
            value: [
              event.target.value,
            ],
          },
        ],
        pageSize: 9999,
        pageNumber: 1,
        sort: -1,
        sortBy: ''
      };
      const result = await pincodeApi.getPincodesByPaginationAndFilters(paginationObject);
      if (result?.responseData?.totalRecords) {
        const isAlreadyExist = result?.responseData?.records;
        setPincodeDetails(isAlreadyExist);
        formik.handleChange(event);
        setCheckPincode(false);
        await formik.setFieldError('pincode', null);
      } else {
        setCheckPincode(true);
        await formik.setFieldError('pincode', 'Unavailable Pincode');
        setAreas([]);
        formik.setFieldValue('city', '');
        formik.setFieldValue('area', '');
        formik.setFieldValue('state', '');
        formik.setFieldTouched('area', false);
      }
    } else {
      await formik.setFieldTouched('pincode', false);
      setAreas([]);
      formik.setFieldValue('city', '');
      formik.setFieldValue('area', '');
      formik.setFieldValue('state', '');
      formik.setFieldTouched('area', false);
    }
  };

  const addressTypeHandler = (value: AddressType) => {
    formik.setFieldValue('addressType', value);
  };

  const renderAddressTypeRadio = (menuType: AddressType) => (
    <>

      <Radio
        checked={menuType === formik.values.addressType}
        onChange={() => addressTypeHandler(menuType)}
        sx={{
          height: '40px',
          px: 0
        }}
      />
      <Typography
        variant="body2"
        sx={{
          height: '40px',
          p: 1,
          pr: 2
        }}
      >
        {menuType}
      </Typography>

    </>
  );

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      onClose={closeDialog}
      open={open}
    >
      <DialogTitle>
        Add Address
        <IconButton
          sx={{
            position: 'absolute',
            right: 8,
            top: 8
          }}
          color="inherit"
          onClick={(e) => closeDialog(e, '')}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid
          container
          direction="row"
          mb={2}
        >
          <Grid
            item
            sm={3}
            md={2}
            xs={12}
          >
            <Typography
              sx={{
                pt: '4px'
              }}
              variant="subtitle1"
              display="inline-flex"
              flexDirection="row"
            >
              Address Type
              <Typography
                flexDirection="inherit"
                color="red"
              >
                {' '}
                *
              </Typography>
            </Typography>
          </Grid>
          <Grid
            item
            sm={9}
            md={10}
            xs={12}
            container
            direction="row"
          >

            {renderAddressTypeRadio(AddressType.HOME)}
            {renderAddressTypeRadio(AddressType.WORK)}
            {renderAddressTypeRadio(AddressType.OTHER)}

          </Grid>
          {formik.values.addressType === AddressType.OTHER && (
          <Grid
            item
            md={4}
          >
            <Grid
              container
            >
              <Grid
                item
                md={12}
                sm={12}
                xs={12}
              >
                <InputField
                  error={Boolean(formik.touched.saveAddressAs
                  && formik.errors.saveAddressAs)}
                  fullWidth
                  helperText={formik.touched.saveAddressAs
                  && formik.errors.saveAddressAs}
                  placeholder="Save Address as"
                  name="saveAddressAs"
                  required
                  label="Save Address as"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  sx={{ mb: 1 }}
                  value={formik.values.saveAddressAs}
                />
              </Grid>
            </Grid>

          </Grid>
          )}
        </Grid>
        <Grid
          container
          spacing={3}
        >
          <Grid
            item
            md={4}
            sm={12}
            xs={12}
          >
            <InputField
              error={Boolean(formik.touched.addressLine1
                && formik.errors.addressLine1)}
              fullWidth
              helperText={formik.touched.addressLine1
                && formik.errors.addressLine1}
              placeholder="Enter Address Line 1"
              name="addressLine1"
              required
              label="Address Line 1"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              sx={{ mb: 1 }}
              value={formik.values.addressLine1}
            />
          </Grid>
          <Grid
            item
            md={4}
            sm={12}
            xs={12}
          >
            <InputField
              error={Boolean(formik.touched.addressLine2
                && formik.errors.addressLine2)}
              fullWidth
              helperText={formik.touched.addressLine2
                && formik.errors.addressLine2}
              placeholder="Enter Address Line 2"
              name="addressLine2"
              label="Address Line 2"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              sx={{ mb: 1 }}
              value={formik.values.addressLine2}
            />
          </Grid>
          <Grid
            item
            md={4}
            sm={12}
            xs={12}
          >
            <InputField
              error={Boolean(formik.touched.landmark
                && formik.errors.landmark)}
              fullWidth
              helperText={formik.touched.landmark
                 && formik.errors.landmark}
              placeholder="Enter landmark "
              name="landmark"
              label="Landmark"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              sx={{ mb: 1 }}
              value={formik.values.landmark}
            />
          </Grid>
        </Grid>
        <Grid
          container
          spacing={3}
        >
          <Grid
            item
            md={4}
            sm={12}
            xs={12}
          >
            <>
              <InputField
                error={Boolean(formik.touched.pincode
                  && formik.errors.pincode)}
                fullWidth
                helperText={formik.touched.pincode
                  && formik.errors.pincode}
                placeholder="Enter pincode"
                name="pincode"
                required
                label="Pincode"
                onBlur={formik.handleBlur}
                onChange={handlePincodeChange}
                sx={{ mb: 1 }}
                value={formik.values.pincode}
              />
              { checkPincode && !formik.errors.pincode && (
                <Typography
                  sx={{
                    ml: 2
                  }}
                  variant="caption"
                  color="red"
                >
                  Unavailable Pincode
                </Typography>
              )}
            </>
          </Grid>
          <Grid
            item
            md={4}
            sm={12}
            xs={12}
          >
            <InputField
              error={Boolean(formik.touched.area
                && formik.errors.area)}
              helperText={formik.touched.area
                && formik.errors.area}
              placeholder="Enter Area"
              name="area"
              required
              select
              disabled={!areas?.length}
              label="Area"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              sx={{ mb: 1 }}
              fullWidth
              value={formik.values.area}
            >
              <MenuItem
                key="defaultAreas"
                value={formik.values.area}
              />
              {areas?.map((area) => (
                <MenuItem
                  key={area}
                  value={area}
                >
                  {area}
                </MenuItem>
              ))}
            </InputField>
          </Grid>
          <Grid
            item
            md={4}
            sm={12}
            xs={12}
          >
            <InputField
              error={Boolean(formik.touched.city
                && formik.errors.city)}
              helperText={formik.touched.city
                && formik.errors.city}
              fullWidth
              required
              name="city"
              label="City"
              disabled
              placeholder="Enter city"
              value={formik.values.city}
              sx={{ mb: 1 }}
            />
          </Grid>
        </Grid>
        <Grid
          container
          spacing={3}
        >
          <Grid
            item
            md={4}
            sm={12}
          >
            <InputField
              error={Boolean(formik.touched.state
              && formik.errors.state)}
              helperText={formik.touched.state
              && formik.errors.state}
              fullWidth
              disabled
              required
              name="state"
              label="State"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter state"
              value={formik.values.state}
              sx={{ mb: 1 }}
            />
          </Grid>

        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          alignItems: 'right'
        }}
      >
        <Button
          color="primary"
          onClick={(e) => closeDialog(e, '')}
          variant="text"
        >
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={(formik.isSubmitting || !(formik.isValid && formik.dirty))
            || (formik.values.addressType === AddressType.OTHER
              ? formik.values.saveAddressAs === '' : formik.isSubmitting || !(formik.isValid && formik.dirty))}
          onClick={() => { formik.handleSubmit(); }}
          variant="contained"
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddCustomerAddress.defaultProps = {
  open: false
};

AddCustomerAddress.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  // @ts-ignore
  address: PropTypes.object,
  addAddress: PropTypes.func
};
